import SelectInput from "@builder/components/general/ConditionSelectInput";
import DynamicDataSelectorInput from "@builder/components/general/DynamicSelectorInput";
import OliverText from "@builder/components/general/OliverText";
import TextInput from "@builder/components/general/TextInput";
import { ActionEditorItem, updateStepperActions } from "@builder/store/slices/actionBuilderSlice";
import {
    removeDynamicDataField,
    resetDynamicDataFields,
    setActionDynamicData,
    setCurrentId,
    setDynamicDataType
} from "@builder/store/slices/popupSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { ActiveEntityType, DynamicDataContext } from "@common/build/activeEntityType";
import { ActionType, DynamicField } from "@common/build/types";
import { processWebhookOutput } from "@common/utils/dynamicDataHelpers";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";

const Webhook = () => {
    const { id } = useAppSelector(state => state.actionBuilder.current);
    const actionStep = useAppSelector(state => state.actionBuilder.actionsEditor.find(item => item.id === id));
    const dynamicDataFields = useAppSelector(state => state.popup.dynamicDataFields);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const [webhookUrl, setWebhookUrl] = useState(actionStep?.action?.data?.webhookUrl || "");
    // const [publicKey, setPublicKey] = useState(actionStep?.action?.data?.publicKey || "");
    const [presetData, setPresetData] = useState(actionStep?.action?.data?.presetData || false);
    const [isCustomHook, setIsCustomHook] = useState(actionStep?.action?.data?.isCustomHook || false);
    const [customHookData, setCustomHookData] = useState(actionStep?.action?.data?.customHookData || "");
    const [payloadType, setPayloadType] = useState(actionStep?.action?.data?.payloadType || "Form encoded");
    const [webhookOutput, setWebhookOutput] = useState("");
    const [selectedType, setSelectedType] = useState(actionStep?.action?.data?.presetType || "");
    const storeData = useAppSelector(state => state.storeData?.data);

    const [localDynamicFields, setLocalDynamicFields] = useState<DynamicField[]>(() => {
        const fields = actionStep?.action?.data?.dynamicDataFields || [];
        fields.forEach((field: DynamicField) => {
            if (field.type && (field.type as string) !== "Custom entry") {
                dispatch(setDynamicDataType({ id: field.id, type: field.type }));
                if (field.dynamicData) {
                    dispatch(setActionDynamicData({ id: field.id, data: field.dynamicData }));
                }
            }
        });
        return fields;
    });

    useEffect(() => {
        const output = processWebhookOutput(webhookUrl, localDynamicFields, payloadType, t, true, storeData);
        setWebhookOutput(output);
    }, [webhookUrl, localDynamicFields, payloadType, t, storeData]);

    useEffect(() => {
        const updatedFields = localDynamicFields.map(field => {
            const dynamicDataField = dynamicDataFields[field.id];
            if (!dynamicDataField) return field;

            return {
                ...field,
                type: (field.type as string) === "Custom entry" ? field.type : dynamicDataField.dynamicDataType,
                dynamicData: (field.type as string) === "Custom entry" ? null : dynamicDataField.actionDynamicData,
                value: (field.type as string) === "Custom entry" ? field.value : ""
            };
        });

        if (JSON.stringify(updatedFields) !== JSON.stringify(localDynamicFields)) {
            setLocalDynamicFields(updatedFields);
        }
    }, [dynamicDataFields]);

    useEffect(() => {
        const action = {
            type: ActionType.WEBHOOK,
            data: {
                webhookUrl,
                // publicKey,
                presetData,
                presetType: selectedType,
                isCustomHook,
                customHookData,
                payloadType,
                dynamicDataFields: localDynamicFields
            }
        };

        const updatedStep = { ...actionStep, action };
        if (JSON.stringify(updatedStep) !== JSON.stringify(actionStep)) {
            dispatch(updateStepperActions({ id, item: updatedStep as ActionEditorItem }));
        }
    }, [webhookUrl, presetData, isCustomHook, customHookData, payloadType, selectedType, localDynamicFields]);

    const handlePayloadTypeChange = (newType: string) => {
        setPayloadType(newType);
        if (newType === "JSON") {
            setLocalDynamicFields([]);
            setPresetData(false);
            setIsCustomHook(false);
            setCustomHookData("");
            dispatch(resetDynamicDataFields());
        }
    };

    const addDynamicDataField = () => {
        const newFieldId = v4();
        const newField = { id: newFieldId, name: "", type: null, value: "" };
        setLocalDynamicFields(prevFields => [...prevFields, newField]);
        dispatch(setDynamicDataType({ id: newFieldId, type: null }));
    };

    const removeDynamicData = (fieldId: string) => {
        setLocalDynamicFields(prevFields => prevFields.filter(field => field.id !== fieldId));
        dispatch(removeDynamicDataField(fieldId));
    };

    const updateDynamicField = (fieldId: string, updates: Partial<DynamicField>) => {
        setLocalDynamicFields(prevFields =>
            prevFields.map(field => {
                if (field.id !== fieldId) return field;
                const updatedField = { ...field, ...updates };

                if ((updates.type as string) === "Custom entry") {
                    dispatch(removeDynamicDataField(fieldId));
                    updatedField.dynamicData = null;
                }

                return updatedField;
            })
        );
    };

    return (
        <div className="function-add-cart-note-wrapper">
            <div className="function-add-cart-note">
                <SelectInput
                    title="Payload type"
                    layout="column"
                    value={payloadType}
                    callback={handlePayloadTypeChange}
                    options={["Form encoded", "JSON", "Plain text"]}
                    placeholder="Select payload type"
                />
                <TextInput
                    title="Webhook URL Endpoint"
                    callback={setWebhookUrl}
                    value={webhookUrl}
                    className="actions-tab-input"
                    placeholder="https://www.example.com/data"
                />
                {/* <TextInput
                    title="Public key"
                    callback={setPublicKey}
                    value={publicKey}
                    className="actions-tab-input"
                    placeholder="Enter public key"
                /> */}

                <TextInput
                    title="Webhook output"
                    callback={setWebhookOutput}
                    value={webhookOutput}
                    className="actions-tab-input"
                    placeholder="https://www.example.com/data"
                    disabled
                />
                <div style={{ height: "1px", backgroundColor: "#e1e1e1", margin: "8px 0" }} />

                <OliverText text="Webhook data" size="s" weight="medium-heavy" translatePrefix="popup.actionBuilder." />

                {payloadType !== "JSON" ? (
                    <div className="dynamic-data-fields-wrapper">
                        <>
                            {localDynamicFields.map((field, index) => (
                                <>
                                    <div className="dynamic-data-fields" key={field.id} onClick={() => dispatch(setCurrentId(field.id))}>
                                        <div style={{ display: "flex", justifyContent: "end" }}>
                                            <img
                                                src={"./trash.svg"}
                                                style={{ width: "20px", height: "20px" }}
                                                className="action-menu-delete"
                                                onClick={() => removeDynamicData(field.id)}
                                            />
                                        </div>

                                        <SelectInput
                                            title=""
                                            layout="column"
                                            value={(field.type as string) || ""}
                                            callback={type => {
                                                const newType = type as DynamicDataContext;
                                                updateDynamicField(field.id, {
                                                    type: newType,
                                                    value: (newType as string) === "Custom entry" ? field.value : "",
                                                    dynamicData: null
                                                });
                                                if ((newType as string) !== "Custom entry") {
                                                    dispatch(setDynamicDataType({ id: field.id, type: newType }));
                                                }
                                            }}
                                            options={[
                                                "Custom entry",
                                                "general",
                                                ActiveEntityType.PRODUCT,
                                                ActiveEntityType.CART,
                                                ActiveEntityType.ORDER,
                                                ActiveEntityType.CUSTOMER
                                            ]}
                                            placeholder="Select data type"
                                        />
                                        <TextInput
                                            title=""
                                            callback={value => updateDynamicField(field.id, { name: value })}
                                            value={field.name}
                                            className="actions-tab-input"
                                            placeholder="Enter parameter"
                                        />
                                        {(field.type as string) === "Custom entry" ? (
                                            <TextInput
                                                title=""
                                                callback={value => updateDynamicField(field.id, { value })}
                                                value={field.value}
                                                className="actions-tab-input"
                                                placeholder="Enter your value"
                                            />
                                        ) : (
                                            <DynamicDataSelectorInput
                                                customClassName="action-dd"
                                                hideTitle
                                                actionDynamicDataType={field.type}
                                                actionDynamicDataId={field.id}
                                                disabled={!field.type}
                                            />
                                        )}
                                    </div>
                                    {index < localDynamicFields.length - 1 && (
                                        <div style={{ textAlign: "center", paddingTop: "0px" }}>
                                            <OliverText text="And" size="s" weight="medium" style={{ width: "100%" }} />
                                        </div>
                                    )}
                                </>
                            ))}
                        </>
                        <div style={{ display: "flex", justifyContent: "center", marginTop: localDynamicFields.length ? "24px" : "0" }}>
                            <div onClick={addDynamicDataField} className="add-dyna-button">
                                {localDynamicFields.length ? "Add" : "Add data"}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="dynamic-data-fields-wrapper">
                        {!presetData && (
                            <div style={{ display: "flex", justifyContent: "center" }}>
                                <div onClick={() => setPresetData(!presetData)} className="add-dyna-button">
                                    Add preset data
                                </div>
                            </div>
                        )}
                        {presetData && (
                            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                                <div style={{ display: "flex", justifyContent: "end" }}>
                                    <img
                                        src={"./trash.svg"}
                                        style={{ width: "20px", height: "20px" }}
                                        className="action-menu-delete"
                                        onClick={() => {
                                            setSelectedType("");
                                            setPresetData(!presetData);
                                        }}
                                    />
                                </div>
                                <SelectInput
                                    title=""
                                    layout="column"
                                    value={selectedType}
                                    callback={type => {
                                        setSelectedType(type);
                                    }}
                                    options={[ActiveEntityType.PRODUCT, ActiveEntityType.CART, ActiveEntityType.ORDER, ActiveEntityType.CUSTOMER]}
                                    placeholder="Select data"
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Webhook;
